import type { Map as LeafletMap } from "leaflet"
import { useEffect } from "react"
import type { CSSProperties, PointerEvent, ReactElement, ReactNode, RefObject } from "react"
import { useMap } from "react-leaflet"
import { useCallbackSafeState } from "../../../hooks"
import { disablePanning, enablePanning } from "../../../utils"

export type DragContext = {
  isDragging: boolean
  isHandleDragging: boolean
  startX: number
  startY: number
  currentX: number
  currentY: number
  elementStartX: number
  elementStartY: number
}

export interface IFreeDraggableProps {
  onMove?: (position: { x: number; y: number }) => void
  children?: ReactNode
  handleRef?: RefObject<HTMLElement>
}

export function FreeDraggable(props: IFreeDraggableProps): ReactElement {
  const { children, handleRef, onMove } = props

  const [style, setStyle] = useCallbackSafeState<CSSProperties>({
    position: "relative",
    left: 0,
    top: 0
  })

  const [context, setContext] = useCallbackSafeState<DragContext>({
    isDragging: false,
    isHandleDragging: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    elementStartX: 0,
    elementStartY: 0
  })

  const map: LeafletMap = useMap()

  useEffect(() => {
    window.addEventListener("pointerup", onDragEnd)
    window.addEventListener("pointermove", onDrag)

    return () => {
      window.removeEventListener("pointerup", onDragEnd)
      window.removeEventListener("pointermove", onDrag)
    }
  }, [])

  useEffect(() => {
    if (handleRef?.current) {
      handleRef.current.addEventListener("pointerdown", onHandleDragStart)
    }

    return () => {
      if (handleRef?.current) {
        handleRef.current.removeEventListener("pointerdown", onHandleDragStart)
      }
    }
  }, [handleRef])

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    updateStyle()
  }, [context()])

  function onHandleDragStart(): void {
    setContext({ ...context(), isHandleDragging: true })
  }

  function onDragStart(event: PointerEvent<HTMLDivElement> | globalThis.PointerEvent): void {
    disableMap()
    const { clientX, clientY } = event

    setContext({
      ...context(),
      isDragging: true,
      startX: clientX,
      startY: clientY,
      currentX: clientX,
      currentY: clientY,
      elementStartX: Number(style().left),
      elementStartY: Number(style().top)
    })
  }

  function onDrag(event: globalThis.PointerEvent): void {
    if (context().isDragging && (handleRef ? context().isHandleDragging : true)) {
      const { clientX, clientY } = event

      setContext({ ...context(), currentX: clientX, currentY: clientY })
      if (onMove) onMove({ x: clientX, y: clientY })
    }
  }

  function onDragEnd(): void {
    enableMap()
    setContext({
      ...context(),
      isDragging: false,
      isHandleDragging: false
    })
  }

  function updateStyle(): void {
    const { elementStartX, elementStartY, currentX, currentY, startX, startY } = context()

    const deltaX: number = currentX - startX
    const deltaY: number = currentY - startY

    const newX: number = elementStartX + deltaX
    const newY: number = elementStartY + deltaY

    setStyle({ ...style(), top: newY, left: newX })
  }

  function disableMap(): void {
    disablePanning(map)
  }

  function enableMap(): void {
    enablePanning(map)
  }

  return (
    <div style={style()} onPointerDown={onDragStart}>
      {children}
    </div>
  )
}
